import { BaseCategory } from "./BaseCategory";
import { DecorationStyle, ElementTextBlockPositionType, HeaderPositionType, PaletteColorType, TextStyleType } from "../constants";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class FunnelCategory extends BaseCategory {
    get name() {
        return "Funnel";
    }

    get type() {
        return SlideCategories.FUNNEL;
    }

    get icon() {
        return null;
    }

    get description() {
        return `The content describes a hierarchical structure, progression, or filtering process. Use for pyramid and funnel diagrams.`;
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Parse the content to determine what to consider as items in the funnel/pyramid and list those items in order. Separate any additional content into the conclusion`;
    }

    getImagePrompt() {
        return false;
    }

    getModelHints() {
        return {
            funnelType: "pyramid | funnel",
        };
    }

    getPromptSchema(options = {}) {
        return {
            items: this.getItemListSchema(options),
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.items)) {
            return false;
        }
        return true;
    }

    getSlideModels(model, options = {}) {
        return this.buildModels([
            this.buildPyramid(model, options),
            this.buildFunnel(model, options),
            this.buildHorizontalFunnel(model, options),
        ]);
    }

    buildPyramid(model, options = {}) {
        let builder = new SlideModelBuilder("pyramid", model, model.hints?.funnelType === "pyramid");
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        let primary = {
            collectionColor: "colorful",
            baseSize: 47,
            capSize: 5,
            capAlign: "center",
            decorationStyle: DecorationStyle.FILLED,
            inverted: false,
            step: false,
            showDescriptions: true,
            items: model.items.map(item => ({
                text: {
                    blocks: [
                        builder.createBlock(item.title, TextStyleType.TITLE),
                        builder.createBlock(item.description, TextStyleType.BODY),
                    ]
                }
            })),
        };

        builder.createPrimary(primary);

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion, ElementTextBlockPositionType.TRAY);
        }
        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }
        return builder;
    }

    buildFunnel(model, options = {}) {
        let builder = new SlideModelBuilder("funnel", model, model.hints?.funnelType === "funnel");
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        let primary = {
            collectionColor: "colorful",
            baseSize: 47,
            capSize: 5,
            capAlign: "center",
            decorationStyle: DecorationStyle.FILLED,
            items: model.items.map(item => ({
                text: {
                    blocks: [
                        builder.createBlock(item.title, TextStyleType.TITLE),
                        builder.createBlock(item.description, TextStyleType.BODY),
                    ]
                }
            })),
        };

        builder.createPrimary(primary);

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion, ElementTextBlockPositionType.TRAY);
        }
        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }
        return builder;
    }

    buildHorizontalFunnel(model, options = {}) {
        let builder = new SlideModelBuilder("horizontal-funnel", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        let primary = {
            collectionColor: "theme-to-dark",
            showSectionDescriptions: true,
            showValues: true,
            mirror: true,
            straightLines: true,
            funnel: [
                { x: 0, y: 0, hx: 0.36, hy: 0 },
                { x: 1, y: 1, hx: 0.85, hy: 0 },
            ],
            items: model.items.map(item => ({
                value: {
                    blocks: [
                        builder.createBlock(item.title, TextStyleType.TITLE),
                    ].filter(Boolean)
                },
                text: {
                    blocks: [
                        builder.createBlock(item.title, TextStyleType.TITLE),
                        builder.createBlock(item.description, TextStyleType.BODY),
                    ].filter(Boolean)
                }
            })),
        };

        builder.createPrimary(primary);

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion, ElementTextBlockPositionType.TRAY);
        }
        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }
        return builder;
    }
}
